<script lang="ts">
export default {
  name: 'FilterButton',
};
</script>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { ListFilter, Columns4 } from 'lucide-vue-next';

const props = defineProps<{
  active?: boolean;
  title?: string;
  icon?: 'mdi-format-columns';
  dataTestActivator?: string;
  badge?: number;
  dense?: boolean;
}>();

const emit = defineEmits(['open', 'close']);

const menu = ref(false);

function close() {
  menu.value = false;
}

const hasBadge = computed(() => {
  return props.badge !== null && props.badge !== undefined && props.badge > 0;
});

watch(menu, (value) => {
  if (!value) {
    emit('close');
  } else {
    emit('open');
  }
});
</script>

<template>
  <div
    class="filter-button tw:cursor-pointer tw:p-0.5"
    :class="{ active }">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      location="bottom"
      origin="left top"
      transition="none"
      scrim>
      <template #activator="{ props }">
        <div
          class="activator tw:rounded-3xl tw:border tw:border-solid tw:border-gray-400"
          :class="[{ dense: dense }]"
          v-bind="props"
          :data-testid="dataTestActivator || 'filter-button-activator'">
          <ListFilter v-if="!icon" :size="14" />
          <Columns4 v-else-if="icon === 'mdi-format-columns'" :size="14" />
          <span class="activator-text">{{ title }}</span>
          <span
            class="badge tw:bg-primary tw:font-bold tw:text-white"
            v-if="hasBadge"
            >{{ badge }}</span
          >
        </div>
      </template>
      <div class="popup">
        <slot
          name="menu"
          :close="close"></slot>
      </div>
    </v-menu>
  </div>
</template>

<style scoped>
.filter-button {
  flex: 0 1 auto;
  margin-right: 0.5rem;
  user-select: none;
  align-self: center;
}

.activator {
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-column-gap: 0.25rem;
  align-items: center;
  align-content: center;
  background: white;
  line-height: 1;
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
}

.activator.dense {
  padding: 0.2rem 1rem;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  font-weight: 500;
}

.filter-button.active > .activator {
  border-color: var(--v-border-darken5);
  font-weight: 700;
  letter-spacing: 0.01rem;
}

.activator > .activator-text {
  white-space: nowrap;
  position: relative;
  top: 1px;
}

.activator.dense > .activator-text {
  top: 0;
}

.activator:hover {
  background: var(--v-gray-lighten5);
}

.activator:active {
  background: var(--v-gray-lighten4);
}

.popup {
  background: white;
}

.badge {
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  line-height: 1.5rem;
  font-size: 75%;
  text-align: center;
}
</style>
